/* ==========================================================================
   Humbra Firefly PDP behaviour

   Loaded once (deferred) by snippets/humbra-firefly-head.liquid. Because that
   snippet is rendered by every Firefly section, the file may be requested by
   several script tags on one page — the guard below makes execution idempotent,
   and every init function marks the nodes it has already wired so re-running is
   always safe. Re-inits on shopify:section:load for the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  if (window.HumbraFirefly) {
    return;
  }

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(root, selector, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), fn);
  }

  /* Mark a node as wired. Returns false if it already was. */
  function claim(el, key) {
    var attr = 'data-ff-' + key + '-ready';
    if (el.hasAttribute(attr)) {
      return false;
    }
    el.setAttribute(attr, '');
    return true;
  }

  function play(video) {
    var p = video.play();
    if (p && p.catch) {
      p.catch(function () {});
    }
  }

  /* ------------------------------------------------------------------------
     Video: hard-mute everything, autoplay backgrounds, scroll-gate the rest.
     Some browsers (and the Shopify editor preview) un-mute on reload, which
     would autoplay audio — so mute is re-asserted on every relevant event.
     ------------------------------------------------------------------------ */

  function forceMute(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.setAttribute('muted', '');
  }

  /* A section with both a desktop and a mobile video ships the tag with no src
     and both URLs as data attributes (see snippets/humbra-firefly-media.liquid),
     so only the file that matches the viewport is ever fetched. Resolved once,
     on the first pass: swapping on resize would restart playback mid-scroll, and
     a phone crossing 820px means an orientation change, not a new visitor.
     Videos that carry a server-written src fall straight through. */
  function resolveVideoSource(video) {
    var mobile = video.getAttribute('data-ff-src-mobile');
    var desktop = video.getAttribute('data-ff-src');
    if (!mobile && !desktop) return;

    var max = video.getAttribute('data-ff-mobile-max') || '820';
    var isMobile = window.matchMedia('(max-width: ' + max + 'px)').matches;

    /* data-ff-mobile-off: the section shows a still image below the breakpoint
       and CSS hides this tag there. Returning before the src is set is the part
       that matters — it means a phone never requests the video file. */
    if (isMobile && video.hasAttribute('data-ff-mobile-off')) return;

    var useMobile = mobile && isMobile;
    var poster = useMobile && video.getAttribute('data-ff-poster-mobile');
    if (poster) {
      video.setAttribute('poster', poster);
    }

    var src = useMobile ? mobile : desktop || mobile;
    if (src && video.getAttribute('src') !== src) {
      video.setAttribute('src', src);
      /* Explicit, matching the difference-card swap below: setting src is meant
         to kick off resource selection on its own, but load() makes the start of
         playback the same in every browser. */
      video.load();
    }
  }

  function initVideos(root) {
    each(root, 'video[data-ff-video]', function (video) {
      if (!claim(video, 'video')) return;

      resolveVideoSource(video);
      forceMute(video);
      ['play', 'playing', 'loadeddata', 'volumechange'].forEach(function (ev) {
        video.addEventListener(ev, function () {
          forceMute(video);
        });
      });

      if (video.hasAttribute('data-ff-scrollvid')) return;
      if (REDUCE) return;
      play(video);
    });

    var scrollVids = (root || document).querySelectorAll('video[data-ff-scrollvid]');
    if (!scrollVids.length || REDUCE || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            forceMute(entry.target);
            play(entry.target);
          } else {
            entry.target.pause();
          }
        });
      },
      { threshold: [0, 0.55, 0.8] }
    );

    Array.prototype.forEach.call(scrollVids, function (video) {
      if (!claim(video, 'scrollvid')) return;
      observer.observe(video);
    });
  }

  /* ------------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------------ */

  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('.ff-reveal');
    if (!nodes.length) return;

    if (REDUCE || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, function (el) {
        el.classList.add('ff-is-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('ff-is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
    );

    Array.prototype.forEach.call(nodes, function (el) {
      if (!claim(el, 'reveal')) return;
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     "The difference" — click a card, the stage swaps to its media
     ------------------------------------------------------------------------ */

  function initDifference(root) {
    each(root, '[data-ff-diff]', function (section) {
      if (!claim(section, 'diff')) return;

      var cards = section.querySelectorAll('[data-ff-diff-card]');
      var stageVideo = section.querySelector('[data-ff-diff-video]');
      var stageImg = section.querySelector('[data-ff-diff-img]');
      var eyebrow = section.querySelector('[data-ff-diff-eyebrow]');
      var caption = section.querySelector('[data-ff-diff-caption]');
      if (!cards.length) return;

      function select(card) {
        Array.prototype.forEach.call(cards, function (c) {
          c.setAttribute('aria-selected', c === card ? 'true' : 'false');
        });

        var videoUrl = card.getAttribute('data-video') || '';
        var imgUrl = card.getAttribute('data-image') || '';

        /* The stage is display:none on mobile — don't touch (or download) it. */
        var stageVisible = stageVideo && stageVideo.offsetParent !== null;

        if (stageVideo) {
          if (videoUrl) {
            stageVideo.hidden = false;
            if (stageVisible && stageVideo.getAttribute('src') !== videoUrl) {
              stageVideo.setAttribute('src', videoUrl);
              stageVideo.load();
            }
            if (stageVisible) {
              forceMute(stageVideo);
              if (!REDUCE) play(stageVideo);
            }
          } else {
            stageVideo.pause();
            stageVideo.hidden = true;
          }
        }

        if (stageImg) {
          /* Fall back to the card's still when it has no video of its own. */
          var showImg = !videoUrl && imgUrl;
          stageImg.hidden = !showImg;
          if (showImg && stageImg.getAttribute('src') !== imgUrl) {
            stageImg.setAttribute('src', imgUrl);
          }
        }

        if (eyebrow) eyebrow.textContent = card.getAttribute('data-eyebrow') || '';
        if (caption) caption.textContent = card.getAttribute('data-caption') || '';
      }

      Array.prototype.forEach.call(cards, function (card) {
        card.addEventListener('click', function () {
          select(card);
        });
      });

      var initial = section.querySelector('[data-ff-diff-card][aria-selected="true"]') || cards[0];
      select(initial);
    });
  }

  /* ------------------------------------------------------------------------
     FAQ accordion (single-open, matching the mockup)
     ------------------------------------------------------------------------ */

  function initFaq(root) {
    each(root, '[data-ff-faq]', function (list) {
      if (!claim(list, 'faq')) return;

      var items = list.querySelectorAll('[data-ff-faq-item]');

      Array.prototype.forEach.call(items, function (item) {
        var btn = item.querySelector('[data-ff-faq-btn]');
        var panel = item.querySelector('[data-ff-faq-panel]');
        var sign = item.querySelector('[data-ff-faq-sign]');
        if (!btn) return;

        btn.addEventListener('click', function () {
          var willOpen = !item.classList.contains('ff-is-open');

          Array.prototype.forEach.call(items, function (other) {
            other.classList.remove('ff-is-open');
            var oBtn = other.querySelector('[data-ff-faq-btn]');
            var oSign = other.querySelector('[data-ff-faq-sign]');
            if (oBtn) oBtn.setAttribute('aria-expanded', 'false');
            if (oSign) oSign.textContent = '+';
          });

          if (willOpen) {
            item.classList.add('ff-is-open');
            btn.setAttribute('aria-expanded', 'true');
            /* True minus (U+2212), matching the Liquid. */
            if (sign) sign.textContent = '−';
          }

          if (panel) {
            /* nothing else to do — CSS drives visibility off .ff-is-open */
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Buy box: variant swatches -> price, media, availability, hidden input.
     Variant data is rendered by Liquid so prices stay correctly formatted for
     the store's currency without any JS money formatting.
     ------------------------------------------------------------------------ */

  /* Buy box carousel.

     Only present when the hero section carries Buy box slide blocks. Slide one
     is the live variant image, which initBuybox already repaints when the
     colour changes, so this deliberately does not own that node — it only moves
     the track. Changing colour snaps back to slide one, otherwise the swatch
     would repaint a slide the shopper cannot see. */
  function initCarousel(root) {
    each(root, '[data-ff-carousel]', function (car) {
      if (!claim(car, 'carousel')) return;

      var track = car.querySelector('[data-ff-carousel-track]');
      if (!track) return;

      var dots = car.querySelectorAll('[data-ff-carousel-dot]');
      var count = parseInt(car.getAttribute('data-count'), 10);
      if (!count || count < 2) return;

      var index = 0;

      function go(next) {
        /* Wraps in both directions, so the arrows never dead-end. The extra
           `+ count` is because JS % keeps the sign of the left operand. */
        index = ((next % count) + count) % count;
        track.style.transform = 'translateX(-' + index * 100 + '%)';
        Array.prototype.forEach.call(dots, function (dot, i) {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      var prev = car.querySelector('[data-ff-carousel-prev]');
      var next = car.querySelector('[data-ff-carousel-next]');
      if (prev) prev.addEventListener('click', function () { go(index - 1); });
      if (next) next.addEventListener('click', function () { go(index + 1); });

      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.addEventListener('click', function () { go(i); });
      });

      /* Slide one is the variant image, so a colour change has to bring it back
         into view — otherwise the swatch repaints a slide the shopper cannot
         see. The broadcast is document-level and carries the product it came
         from, so filter it the same way every other listener in this file does:
         a page with two of these must not have one reset the other. */
      var box = car.closest ? car.closest('[data-ff-buybox]') : null;
      document.addEventListener('firefly:variant:change', function (evt) {
        if (!box || !evt.detail) return;
        if (!sameProduct(evt.detail.product, box.getAttribute('data-ff-product-id'))) return;
        go(0);
      });

      /* Swipe. Horizontal intent only — a mostly-vertical drag is the page
         being scrolled and must not steal the gesture. */
      var x0 = null;
      var y0 = null;
      track.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true });
      track.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          go(dx < 0 ? index + 1 : index - 1);
        }
        x0 = null;
        y0 = null;
      }, { passive: true });

      go(0);
    });
  }

  /* Remove the restock app's "Notify me" button from a buy box in preview mode.

     CSS alone cannot do this: the app writes style="display:flex !important"
     onto the node, and an inline !important beats any stylesheet declaration.
     The app also injects after load and re-injects on variant change, so a
     one-shot removal is not enough either — hence an observer.

     Scoped to the buy box that asked for it. The live PDP is sold out too and
     is still collecting addresses through that button; this must never reach
     it. */
  function initPreviewHideRestock(root) {
    each(root, '[data-ff-force-available]', function (box) {
      if (!claim(box, 'hiderestock')) return;

      function strip() {
        var btns = box.querySelectorAll('.restock-alerts-notify-button');
        Array.prototype.forEach.call(btns, function (btn) {
          /* Take the wrapper when the app made one solely for this button, so
             no empty box is left holding margin. */
          var portal = btn.closest ? btn.closest('.nm-portal') : null;
          var target = (portal && portal.children.length === 1) ? portal : btn;
          if (target.parentNode) target.parentNode.removeChild(target);
        });
      }

      strip();
      if (!window.MutationObserver) return;
      new MutationObserver(strip).observe(box, { childList: true, subtree: true });
    });
  }

  /* Quantity stepper.

     The input stays the source of truth -- it is the one the form posts -- so
     the buttons only ever read and write it. Clamped on blur rather than on
     input, so a half-typed number is not rewritten under the cursor. */
  function initQuantity(root) {
    each(root, "[data-ff-qty]", function (wrap) {
      if (!claim(wrap, "qty")) return;

      var input = wrap.querySelector("[data-ff-qty-input]");
      if (!input) return;
      var down = wrap.querySelector("[data-ff-qty-down]");
      var up = wrap.querySelector("[data-ff-qty-up]");

      function bound(attr, fallback) {
        var n = parseInt(input.getAttribute(attr), 10);
        return isNaN(n) ? fallback : n;
      }

      function read() {
        var n = parseInt(input.value, 10);
        return isNaN(n) ? bound("min", 1) : n;
      }

      /* silent on the opening pass: dispatching change before anything has
         happened would tell the rest of the page the shopper acted. */
      function write(n, silent) {
        var lo = bound("min", 1);
        var hi = bound("max", null);
        if (n < lo) n = lo;
        if (hi !== null && n > hi) n = hi;

        input.value = n;
        if (down) down.disabled = n <= lo;
        if (up) up.disabled = hi !== null && n >= hi;

        if (!silent) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      if (down) {
        down.addEventListener("click", function () { write(read() - 1); });
      }
      if (up) {
        up.addEventListener("click", function () { write(read() + 1); });
      }
      input.addEventListener("blur", function () { write(read()); });

      write(read(), true);
    });
  }

  /* Hold the theme button label against an app that rewrites it.

     STOQ reuses this button as its preorder CTA -- it relabels it rather than
     replacing it, which is why the label flashes the theme value and then
     changes. The wording matters here: the whole page says reserve, and the
     reservation is not a completed preorder until the balance is paid.

     Which label is correct depends on state, and initBuybox owns that: the
     default text while the variant is buyable, the sold-out text when it is
     not. So this reads the button's own disabled state rather than assuming,
     and never masks a variant going unavailable.

     The observer is disconnected while writing, so restoring the text cannot
     re-trigger it -- an app that insisted on its own label would otherwise
     leave the two of us trading writes forever. */
  function initAtcTextLock(root) {
    each(root, "[data-ff-lock-atc-text]", function (box) {
      if (!claim(box, "atclock")) return;

      var atc = box.querySelector("[data-ff-atc]");
      var label = box.querySelector("[data-ff-atc-text]");
      if (!atc || !label || !window.MutationObserver) return;

      function wanted() {
        var key = atc.disabled ? "data-soldout-text" : "data-default-text";
        return label.getAttribute(key) || "";
      }

      var observer = new MutationObserver(function () {
        var want = wanted();
        if (!want || label.textContent.trim() === want) return;
        observer.disconnect();
        label.textContent = want;
        observer.observe(label, { childList: true, characterData: true, subtree: true });
      });

      observer.observe(label, { childList: true, characterData: true, subtree: true });

      /* The app may already have relabelled it before this ran. */
      var want = wanted();
      if (want && label.textContent.trim() !== want) {
        observer.disconnect();
        label.textContent = want;
        observer.observe(label, { childList: true, characterData: true, subtree: true });
      }
    });
  }

  /* Go to the cart (or checkout) once the item is actually in it.

     The preorder app performs the add with its own request and announces
     nothing, so there is no event to listen for -- and the theme's own
     ajaxProduct:added only fires for adds the theme made. Rather than guess at
     the app's internals or trust a timer, this watches the only thing that is
     authoritative either way: the cart. It reads the count before the click and
     navigates on the first poll that shows it risen.

     Bounded deliberately. A failed or cancelled add must not leave the page
     navigating away several seconds later, so polling gives up rather than
     assuming success. */
  var ADD_POLL_MS = 350;
  var ADD_POLL_TRIES = 20;

  function initAddRedirect(root) {
    each(root, "[data-ff-add-redirect]", function (box) {
      if (!claim(box, "addredirect")) return;

      var dest = box.getAttribute("data-ff-add-redirect");
      var atc = box.querySelector("[data-ff-atc]");
      if (!dest || !atc || !window.fetch) return;

      var going = false;
      function go() {
        if (going) return;
        going = true;
        window.location.href = dest;
      }

      function count() {
        return fetch(localePath("/cart.js"), { credentials: "same-origin" })
          .then(function (r) { return r.json(); })
          .then(function (c) { return c.item_count; })
          .catch(function () { return null; });
      }

      /* Adds the theme itself made say so, so take the shortcut when offered. */
      document.addEventListener("ajaxProduct:added", go);

      atc.addEventListener("click", function () {
        if (atc.disabled) return;
        count().then(function (before) {
          if (before === null) return;
          var tries = 0;
          (function poll() {
            if (going || ++tries > ADD_POLL_TRIES) return;
            window.setTimeout(function () {
              if (going) return;
              count().then(function (now) {
                if (now !== null && now > before) return go();
                poll();
              });
            }, ADD_POLL_MS);
          })();
        });
      });
    });
  }

  /* Make a secondary CTA press the buy box button rather than add on its own.

     A preorder app hooks one control. Here it relabels the buy box button and
     rides its form, so that button -- not the app's own -- is the thing that
     actually works. Any other product form on the page is a route the app never
     saw, and a click there charges the full price with no deposit.

     Pressing the real button rather than re-implementing the add means the
     deposit, the selected colour, the quantity and the redirect afterwards all
     come along for free, and there is only ever one add-to-cart path to keep
     working.

     If that button is missing or disabled -- a colour that has sold through --
     this scrolls to it instead, so the shopper sees why rather than pressing
     something that silently does nothing. */
  function initProxyAtc(root) {
    each(root, "[data-ff-proxy-atc]", function (btn) {
      if (!claim(btn, "proxyatc")) return;

      btn.addEventListener("click", function () {
        var box = document.querySelector("[data-ff-buybox]");
        var atc = box && box.querySelector("[data-ff-atc]");

        if (!atc || atc.disabled) {
          if (box) {
            box.scrollIntoView({
              behavior: REDUCE ? "auto" : "smooth",
              block: "center"
            });
          }
          return;
        }

        atc.click();
      });
    });
  }

  function initBuybox(root) {
    each(root, '[data-ff-buybox]', function (box) {
      if (!claim(box, 'buybox')) return;

      var dataEl = box.querySelector('[data-ff-variants]');
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      if (!variants || !variants.length) return;

      var idInput = box.querySelector('[data-ff-variant-id]');
      var select = box.querySelector('[data-ff-variant-select]');
      var priceEl = box.querySelector('[data-ff-price]');
      var compareEl = box.querySelector('[data-ff-compare]');
      var imageEl = box.querySelector('[data-ff-variant-image]');
      var valueEl = box.querySelector('[data-ff-variant-value]');
      var inventoryEl = box.querySelector('[data-ff-inventory]');
      var atc = box.querySelector('[data-ff-atc]');
      var atcText = box.querySelector('[data-ff-atc-text]');
      var swatches = box.querySelectorAll('[data-ff-swatch]');
      var forceAvailable = box.hasAttribute('data-ff-force-available');

      function find(id) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(id)) return variants[i];
        }
        return null;
      }

      function apply(variant) {
        if (!variant) return;

        if (idInput) idInput.value = variant.id;
        if (select && select.value !== String(variant.id)) select.value = variant.id;

        if (priceEl) priceEl.innerHTML = variant.price;
        if (compareEl) {
          if (variant.compare_at_price) {
            compareEl.innerHTML = variant.compare_at_price;
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (imageEl && variant.image) {
          imageEl.setAttribute('src', variant.image);
          if (variant.image_alt) imageEl.setAttribute('alt', variant.image_alt);
        }

        /* The panel crops to fill, and the stills are framed differently per
           colour, so the focal point travels with the image. Rendered inline by
           Liquid for the colour shown on load; this keeps it in step after that.
           Guarded because a variant from a section saved before these settings
           existed carries no focal point, and writing "undefined%" here would
           throw the whole declaration away rather than fall back to centre. */
        if (imageEl && variant.focus_x != null && variant.focus_y != null) {
          imageEl.style.objectPosition = variant.focus_x + '% ' + variant.focus_y + '%';
        }

        if (valueEl) valueEl.textContent = variant.title;

        if (inventoryEl) {
          if (variant.inventory_message) {
            inventoryEl.textContent = variant.inventory_message;
            inventoryEl.className = 'ff-buybox__inventory ff-buybox__inventory--' + variant.inventory_state;
            inventoryEl.hidden = false;
          } else {
            inventoryEl.hidden = true;
          }
        }

        /* Preview mode renders the button live against a sold-out product so the
           page can be reviewed before the real one exists. Liquid draws it that
           way; without this the first variant broadcast would put it straight
           back to disabled. Nothing here can make a sold-out variant buyable —
           Shopify still refuses it at checkout. */
        if (atc && !forceAvailable) {
          atc.disabled = !variant.available;
          atc.classList.toggle('ff-is-disabled', !variant.available);
          if (atcText) {
            atcText.textContent = variant.available
              ? atcText.getAttribute('data-default-text')
              : atcText.getAttribute('data-soldout-text');
          }
        }

        Array.prototype.forEach.call(swatches, function (sw) {
          sw.setAttribute(
            'aria-checked',
            String(sw.getAttribute('data-variant-id')) === String(variant.id) ? 'true' : 'false'
          );
        });

        /* Keep the sticky bar, the cloned swatch groups and the final CTA in
           sync with the buy box. The product id rides along so a section
           selling something else can ignore it. */
        document.dispatchEvent(
          new CustomEvent('firefly:variant:change', {
            detail: {
              variant: variant,
              product: box.getAttribute('data-ff-product-id')
            }
          })
        );
      }

      Array.prototype.forEach.call(swatches, function (sw) {
        sw.addEventListener('click', function () {
          apply(find(sw.getAttribute('data-variant-id')));
        });
        sw.addEventListener('keydown', function (evt) {
          if (evt.key !== 'ArrowRight' && evt.key !== 'ArrowLeft') return;
          evt.preventDefault();
          var list = Array.prototype.slice.call(swatches);
          var i = list.indexOf(sw);
          var next = list[evt.key === 'ArrowRight' ? i + 1 : i - 1];
          if (!next) return;
          next.focus();
          apply(find(next.getAttribute('data-variant-id')));
        });
      });

      if (select) {
        select.addEventListener('change', function () {
          apply(find(select.value));
        });
      }

      /* Swatches cloned into the sticky bar and the final CTA ask for a variant
         by id; resolving and applying it stays here, with the variant list. */
      document.addEventListener('firefly:variant:select', function (evt) {
        if (!evt.detail) return;
        apply(find(evt.detail.id));
      });

      /* Hand the form to the theme's ajax cart when the store uses one. */
      var form = box.querySelector('form[action*="/cart/add"]');
      if (
        form &&
        window.theme &&
        theme.AjaxProduct &&
        theme.settings &&
        (theme.settings.cartType === 'drawer' || theme.settings.cartType === 'sticky')
      ) {
        new theme.AjaxProduct(form, '[data-ff-atc]', {
          scopedEventId: 'FireflyBuybox-' + (box.getAttribute('data-section-id') || 'main')
        });
      }

      var current = find(idInput && idInput.value) || variants[0];
      apply(current);
    });
  }

  /* ------------------------------------------------------------------------
     Accessory / bundle add-to-cart forms.
     Handed to the theme's ajax cart when the store uses a drawer or sticky
     cart; otherwise the plain form POST runs and Shopify redirects to /cart.
     ------------------------------------------------------------------------ */

  function initAddons(root) {
    if (
      !window.theme ||
      !theme.AjaxProduct ||
      !theme.settings ||
      (theme.settings.cartType !== 'drawer' && theme.settings.cartType !== 'sticky')
    ) {
      return;
    }

    each(root, 'form[data-ff-addon-form]', function (form) {
      if (!claim(form, 'addon')) return;
      new theme.AjaxProduct(form, '[data-ff-addon-atc]', {
        scopedEventId: 'FireflyAddon'
      });
    });
  }

  /* ------------------------------------------------------------------------
     Variant pickers on the accessory / bundle cards.

     The <select> is always the source of truth: on an accessory card it
     carries name="id" and is what the form submits, so the correct variant is
     added even with JS off. The pills just drive it. On a bundle card the
     picker has a slot number and writes into the matching bundle-item input.
     Prices come pre-formatted from Liquid — no currency maths here.
     ------------------------------------------------------------------------ */

  function initVariantPickers(root) {
    each(root, '[data-ff-vp]', function (picker) {
      if (!claim(picker, 'vp')) return;

      var select = picker.querySelector('[data-ff-vp-select]');
      if (!select) return;

      var pills = picker.querySelectorAll('[data-ff-vp-pill]');
      var card = picker.closest ? picker.closest('.ff-addon') : null;
      var slot = picker.getAttribute('data-vp-slot');

      function sync() {
        var option = select.options[select.selectedIndex];
        if (!option) return;

        Array.prototype.forEach.call(pills, function (pill) {
          pill.setAttribute(
            'aria-checked',
            pill.getAttribute('data-variant-id') === select.value ? 'true' : 'false'
          );
        });

        /* Bundle card: point the matching item input at the chosen variant. */
        if (slot !== null && card) {
          var input = card.querySelector('[data-ff-bundle-item][data-slot="' + slot + '"]');
          if (input) input.value = select.value;
        }

        /* Accessory card: price and button follow the selection. */
        if (card) {
          var priceEl = card.querySelector('[data-ff-card-price]');
          if (priceEl && option.getAttribute('data-price')) {
            priceEl.textContent = option.getAttribute('data-price');
          }

          var available = option.getAttribute('data-available') !== 'false';
          var atc = card.querySelector('[data-ff-addon-atc]');
          if (atc) {
            atc.disabled = !available;
            atc.classList.toggle('ff-is-disabled', !available);
          }
        }
      }

      Array.prototype.forEach.call(pills, function (pill) {
        pill.addEventListener('click', function () {
          if (pill.disabled) return;
          select.value = pill.getAttribute('data-variant-id');
          sync();
        });
        pill.addEventListener('keydown', function (evt) {
          if (evt.key !== 'ArrowRight' && evt.key !== 'ArrowLeft') return;
          evt.preventDefault();
          var list = Array.prototype.filter.call(pills, function (p) {
            return !p.disabled;
          });
          var i = list.indexOf(pill);
          var next = list[evt.key === 'ArrowRight' ? i + 1 : i - 1];
          if (!next) return;
          next.focus();
          select.value = next.getAttribute('data-variant-id');
          sync();
        });
      });

      select.addEventListener('change', sync);

      /* Follow the buy box when this picker is choosing the same product — the
         bundle's Firefly slot. One-way on purpose: a shopper configuring the
         bundle shouldn't have the hero and sticky bar move underneath them, and
         the risk being fixed only runs downhill anyway (colour chosen up the
         page, bundle silently adding a different one).

         The select is the picker's source of truth, so setting it and running
         the existing sync updates the pills, the price and the slot input with
         no separate path to keep in step. */
      document.addEventListener('firefly:variant:change', function (evt) {
        if (!evt.detail || !evt.detail.variant) return;
        if (!sameProduct(evt.detail.product, picker.getAttribute('data-vp-product-id'))) return;

        var id = String(evt.detail.variant.id);
        var option = select.querySelector('option[value="' + id + '"]');
        if (!option || select.value === id) return;

        select.value = id;
        sync();
      });

      sync();
    });
  }

  /* ------------------------------------------------------------------------
     Bundle add-to-cart.

     Two variants in one request means the Cart AJAX API's `items` array, sent
     as JSON — theme.AjaxProduct form-encodes a single variant, so it cannot do
     this. On success we dispatch the theme's own bubbling `ajaxProduct:added`
     event, which is what CartDrawer listens for to rebuild and open itself.

     Any Shopify automatic discount is applied server-side the moment both
     items are in the cart; nothing here has to ask for it. A discount *code*
     on the block is the opt-in belt-and-braces route.
     ------------------------------------------------------------------------ */

  function bundleError(form, data) {
    var existing = form.parentNode.querySelector('[data-ff-bundle-error]');
    if (existing) existing.remove();

    var msg = document.createElement('div');
    msg.className = 'ff-addon__error';
    msg.setAttribute('data-ff-bundle-error', '');
    msg.setAttribute('role', 'alert');
    msg.textContent =
      (data && (data.description || data.message)) || 'Could not add the bundle. Please try again.';
    form.parentNode.insertBefore(msg, form);
  }

  function localePath(path) {
    /* theme.routes.home is locale-aware ("/" or "/en-gb/"). */
    var root = (window.theme && theme.routes && theme.routes.home) || '/';
    return root.replace(/\/$/, '') + path;
  }

  function initBundles(root) {
    each(root, 'form[data-ff-bundle-form]', function (form) {
      if (!claim(form, 'bundle')) return;

      var btn = form.querySelector('[data-ff-bundle-atc]');

      form.addEventListener('submit', function (evt) {
        evt.preventDefault();
        if (form.hasAttribute('data-ff-busy')) return;

        var items = Array.prototype.map
          .call(form.querySelectorAll('[data-ff-bundle-item]'), function (input) {
            return {
              id: parseInt(input.value, 10),
              quantity: parseInt(input.getAttribute('data-quantity'), 10) || 1
            };
          })
          .filter(function (item) {
            return item.id;
          });

        if (!items.length) return;

        form.setAttribute('data-ff-busy', '');
        if (btn) btn.classList.add('btn--loading');

        var existing = form.parentNode.querySelector('[data-ff-bundle-error]');
        if (existing) existing.remove();

        var url = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

        /* Set when we are leaving the page, so the cleanup below leaves the
           button spinning rather than going idle in front of the customer. */
        var redirecting = false;

        fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok || result.data.status === 422) {
              bundleError(form, result.data);
              return;
            }

            var code = form.getAttribute('data-ff-discount-code');
            if (code) {
              var cart = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
              var target =
                localePath('/discount/' + encodeURIComponent(code)) +
                '?redirect=' +
                encodeURIComponent(cart);

              /* Shopify emits product_added_to_cart per line, and the Mida
                 pixel posts each one to its API — a CORS preflight plus a POST,
                 with no keepalive. Navigating the instant the add returns
                 cancels those requests, so bundle add-to-carts go missing from
                 the A/B results. Hold the redirect long enough for the beacon
                 to leave; the delay is not perceptible against a page load. */
              redirecting = true;
              setTimeout(function () {
                window.location.href = target;
              }, 500);
              return;
            }

            var drawer =
              window.theme &&
              theme.settings &&
              (theme.settings.cartType === 'drawer' || theme.settings.cartType === 'sticky');

            if (!drawer) {
              /* No drawer to show the result in — go to the cart page. */
              window.location.href = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
              return;
            }

            form.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: { product: result.data, addToCartBtn: btn },
                bubbles: true
              })
            );

            if (document.body.classList.contains('template-cart')) window.location.reload();
          })
          .catch(function () {
            bundleError(form, null);
          })
          .then(function () {
            /* Mid-redirect the form stays busy: releasing it would idle the
               button for half a second and invite a second add. */
            if (redirecting) return;
            form.removeAttribute('data-ff-busy');
            if (btn) btn.classList.remove('btn--loading');
          });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Colour swatches outside the buy box (sticky bar, final CTA).

     Cloned from the buy box's group rather than re-rendered, so the variants,
     swatch colours and sold-out states are configured in one place — the hero's
     swatch blocks — and can't drift between the three. A clone only asks for a
     variant (firefly:variant:select); the buy box remains the single thing that
     resolves and applies one, and broadcasts the result.
     ------------------------------------------------------------------------ */

  /* Same product, or no opinion either way. */
  function sameProduct(a, b) {
    return !a || !b || String(a) === String(b);
  }

  function initSwatchProxies(root) {
    var source = document.querySelector('[data-ff-swatches]');
    if (!source) return;

    var box = source.closest ? source.closest('[data-ff-buybox]') : null;
    var sourceProduct = box && box.getAttribute('data-ff-product-id');

    each(root, '[data-ff-swatch-clone]', function (target) {
      if (!claim(target, 'swatchclone')) return;

      /* A section pointed at a different product must not offer these colours. */
      if (!sameProduct(sourceProduct, target.getAttribute('data-ff-product-id'))) return;

      var clone = source.cloneNode(true);
      clone.removeAttribute('data-ff-swatches');

      /* Retag the buttons: data-ff-swatch belongs to the buy box's own group,
         which owns the variant list and the form. */
      Array.prototype.forEach.call(clone.querySelectorAll('[data-ff-swatch]'), function (btn) {
        btn.removeAttribute('data-ff-swatch');
        btn.setAttribute('data-ff-swatch-proxy', '');
      });

      target.appendChild(clone);

      target.addEventListener('click', function (evt) {
        var btn = evt.target.closest ? evt.target.closest('[data-ff-swatch-proxy]') : null;
        if (!btn) return;

        document.dispatchEvent(
          new CustomEvent('firefly:variant:select', {
            detail: { id: btn.getAttribute('data-variant-id') }
          })
        );
      });
    });
  }

  /* ------------------------------------------------------------------------
     Keeps everything that isn't the buy box in step with the selected variant:
     the cloned swatch groups, their labels, and the final CTA's hidden input —
     which previously always submitted the default colour no matter what was
     chosen above it.
     ------------------------------------------------------------------------ */

  function initVariantSync() {
    if (!claim(document.documentElement, 'variantsync')) return;

    document.addEventListener('firefly:variant:change', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) return;

      var id = String(variant.id);

      each(document, '[data-ff-swatch], [data-ff-swatch-proxy]', function (btn) {
        btn.setAttribute('aria-checked', btn.getAttribute('data-variant-id') === id ? 'true' : 'false');
      });

      each(document, '[data-ff-variant-value]', function (el) {
        el.textContent = variant.title;
      });

      /* Stock state, wherever it is shown: the buy box, the sticky bar and the
         final CTA. Each element carries the base class its section wants, so
         one message can be styled three ways without this knowing about any of
         them. An empty message means the state is not worth saying — the
         element is hidden rather than emptied, so it takes up no room. */
      each(document, '[data-ff-inventory]', function (el) {
        var scope = el.closest ? el.closest('[data-ff-product-id]') : null;
        if (!sameProduct(evt.detail.product, scope && scope.getAttribute('data-ff-product-id'))) {
          return;
        }

        if (!variant.inventory_message) {
          el.hidden = true;
          return;
        }

        var base = el.getAttribute('data-ff-inventory-class');
        el.textContent = variant.inventory_message;
        if (base) el.className = base + ' ' + base + '--' + variant.inventory_state;
        el.hidden = false;
      });

      each(document, '[data-ff-final-variant]', function (input) {
        /* Never hand a form a variant of a product it isn't selling. */
        if (!sameProduct(evt.detail.product, input.getAttribute('data-ff-product-id'))) return;
        input.value = id;

        /* That button was rendered from the default variant, so now that the
           input follows the selection it also has to follow availability —
           otherwise picking a sold-out colour leaves it submitting one. */
        var form = input.form || (input.closest ? input.closest('form') : null);
        var submit = form && form.querySelector('[type="submit"]');
        if (submit) {
          submit.disabled = !variant.available;
          submit.classList.toggle('ff-is-disabled', !variant.available);
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Sticky buy bar — shows once the hero has scrolled past
     ------------------------------------------------------------------------ */

  /* Both call sites below go through this rather than toggling the class
     directly, so the root class and the measured height can never drift out of
     step with the bar. See .ff-buybar-open in humbra-firefly.css: it moves the
     wishlist app's fixed launcher up out of the bar's way, and needs the bar's
     height because that height is two rows on a phone and one on a desktop. */
  function setBarVisible(bar, visible) {
    if (bar.classList.contains('ff-is-visible') === visible) return;

    bar.classList.toggle('ff-is-visible', visible);
    document.documentElement.classList.toggle('ff-buybar-open', visible);

    /* offsetHeight ignores the transform, so this reads the same whether the
       bar is parked off screen or on it. */
    if (visible) {
      document.documentElement.style.setProperty('--ff-buybar-h', bar.offsetHeight + 'px');
    }
  }

  function initBuybar(root) {
    each(root, '[data-ff-buybar]', function (bar) {
      if (!claim(bar, 'buybar')) return;

      var priceEl = bar.querySelector('[data-ff-buybar-price]');

      /* Rotating the phone re-wraps the bar, so the stored height would be
         stale for as long as the bar stayed up. */
      window.addEventListener(
        'resize',
        function () {
          if (!bar.classList.contains('ff-is-visible')) return;
          document.documentElement.style.setProperty('--ff-buybar-h', bar.offsetHeight + 'px');
        },
        { passive: true }
      );

      document.addEventListener('firefly:variant:change', function (evt) {
        if (priceEl && evt.detail && evt.detail.variant) {
          priceEl.innerHTML = evt.detail.variant.price;
        }
      });

      var sentinel = document.querySelector('[data-ff-hero-sentinel]');

      if (!sentinel || !('IntersectionObserver' in window)) {
        /* No hero on the page (or no IO): fall back to a scroll threshold. */
        var onScroll = function () {
          setBarVisible(bar, (window.scrollY || 0) > window.innerHeight * 0.6);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          setBarVisible(bar, !entries[0].isIntersecting);
        },
        { threshold: 0 }
      );
      observer.observe(sentinel);
    });
  }

  /* ------------------------------------------------------------------------
     "Added" flash on the button that was pressed.

     The theme's sticky cart used to be the confirmation on this page; it is
     suppressed here because it covered the buy bar, so the button says so
     itself and the count bubbles pop (assets/cart-count.js drives those).

     The pressed button is tracked on the way in rather than taken from the
     event: AjaxProduct reports the button belonging to the form it submitted,
     which for the sticky bar is the buy box button scrolled off screen. The
     event's button is the fallback for anything that adds without a click.
     ------------------------------------------------------------------------ */

  var ADD_BUTTONS = '[data-ff-atc], [data-ff-addon-atc], [data-ff-bundle-atc], .ff-buybar__btn';
  var pressed = null;

  function initAddedFlash() {
    if (!claim(document.documentElement, 'addedflash')) return;

    document.addEventListener(
      'click',
      function (evt) {
        var btn = evt.target.closest ? evt.target.closest(ADD_BUTTONS) : null;
        if (btn) pressed = btn;
      },
      true
    );

    /* A failed add keeps its own error message; drop the reference so the next
       success can't flash a button the shopper pressed minutes ago. */
    document.addEventListener('ajaxProduct:error', function () {
      pressed = null;
    });

    document.addEventListener('ajaxProduct:added', function (evt) {
      var btn = pressed || (evt.detail && evt.detail.addToCartBtn);
      pressed = null;

      /* Already flashing: leave the first one to finish. */
      if (!btn || btn.hasAttribute('data-ff-flashing')) return;

      /* A class only. The confirmation is drawn over the label by CSS rather
         than swapped into it: the buy box holds a reference to the label node
         to keep it in step with variant availability, and rewriting innerHTML
         here would detach that node for good. */
      btn.setAttribute('data-ff-flashing', '');
      btn.classList.add('ff-btn--added');

      window.setTimeout(function () {
        btn.classList.remove('ff-btn--added');
        btn.removeAttribute('data-ff-flashing');
      }, 1800);
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function init(root) {
    initVideos(root);
    initReveal(root);
    initDifference(root);
    initFaq(root);
    /* Before initBuybox: it dispatches the opening firefly:variant:change from
       inside its own init, and the final CTA's stock line is empty markup until
       that event lands. Listen after it and the line stays blank until the
       shopper happens to change colour. */
    initVariantSync();
    initBuybox(root);
    initQuantity(root);
    initAtcTextLock(root);
    initAddRedirect(root);
    initProxyAtc(root);
    /* After initBuybox: the clones are copies of the group it owns. */
    initSwatchProxies(root);
    /* After initBuybox: it owns slide one, and the reset listener here has to be
       bound to a buy box that has already announced its opening variant. */
    initCarousel(root);
    initPreviewHideRestock(root);
    initVariantPickers(root);
    initAddons(root);
    initBundles(root);
    initBuybar(root);
    initAddedFlash();
  }

  window.HumbraFirefly = { init: init };

  document.documentElement.classList.remove('ff-no-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor: a re-rendered section arrives with un-wired nodes. */
  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
